import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, ArrowRight, Target, Eye, Award, Shield, Zap, Users } from "lucide-react";

const VALUES = [
  { Icon: Shield, title: "Quality Governance", desc: "Stringent verification pipelines ensuring zero out-of-box failure rates across all product categories." },
  { Icon: Award, title: "Brand Excellence", desc: "Partnered with 11+ globally trusted brands — Dell, HP, ATEN, Hikvision, Honeywell and more." },
  { Icon: Zap, title: "Agile Supply Chain", desc: "Localized logistics and fast dispatch capability to ensure on-time delivery for every order." },
  { Icon: Users, title: "B2B Partnership Focus", desc: "End-to-end partner satisfaction with tailored pricing and dedicated support for each client segment." },
];

const TIMELINE = [
  { year: "2017", event: "Jain Infotech founded in Secunderabad, Telangana" },
  { year: "2019", event: "Expanded product portfolio to include CCTV & surveillance solutions" },
  { year: "2021", event: "Onboarded ATEN, Hikvision & Honeywell as authorized distribution partners" },
  { year: "2023", event: "Crossed 500+ active product SKUs and 1000+ satisfied clients" },
  { year: "2025", event: "7+ years of operational excellence and supply chain trust" },
];

export default function About() {
  return (
    <div>
      {/* Hero */}
      <section className="bg-[#2D3748] py-14 px-6" data-testid="about-hero">
        <div className="max-w-7xl mx-auto">
          <nav className="text-xs text-gray-500 mb-4">
            <Link to="/" className="hover:text-gray-300 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-300">About Us</span>
          </nav>
          <h1 className="font-heading text-4xl lg:text-5xl font-black text-white mb-3">About Jain Infotech</h1>
          <p className="text-gray-400 text-base max-w-2xl leading-relaxed">
            Premier IT infrastructure and surveillance solutions distributor serving corporates, system integrators, and institutions since 2017.
          </p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-16 bg-white" data-testid="company-story-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-[#0F62FE] bg-blue-50 border border-blue-200 px-3 py-1.5 mb-5">Our Story</span>
              <h2 className="font-heading text-3xl lg:text-4xl font-black text-[#111827] mb-5 leading-tight">
                7+ Years Building IT Infrastructure Trust
              </h2>
              <p className="text-[#4B5563] text-base leading-relaxed mb-5">
                Established in 2017, Jain Infotech has grown from a focused cables distributor to a comprehensive IT infrastructure and surveillance solutions partner. Based in Secunderabad's Chenoy Trade Center, we serve enterprises, government bodies, IT dealers, and system integrators across Telangana and beyond.
              </p>
              <p className="text-[#4B5563] text-base leading-relaxed mb-6">
                Our strength lies in maintaining an exhaustive inventory of 2000+ products — from HDMI cables and Cat6 networking gear to Hikvision CCTV systems and ATEN enterprise KVM solutions — backed by strict quality governance and competitive pricing.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" data-testid="about-contact-btn" className="inline-flex items-center gap-2 bg-[#0F62FE] hover:bg-[#0052CC] text-white font-semibold px-5 py-3 transition-all duration-200 hover:shadow-lg">
                  Get in Touch <ArrowRight size={14} />
                </Link>
                <a
                  href="/assets/jain-infotech-brochure.pdf"
                  download
                  data-testid="about-download-brochure-btn"
                  className="inline-flex items-center gap-2 border border-[#2D3748] text-[#2D3748] hover:bg-gray-50 font-semibold px-5 py-3 transition-all duration-200"
                >
                  <Download size={14} /> Download Brochure
                </a>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="relative">
                <img
                  src="https://images.pexels.com/photos/6950031/pexels-photo-6950031.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Jain Infotech Team"
                  className="w-full h-[400px] object-cover shadow-xl"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-[#2D3748]/80 backdrop-blur-sm p-5">
                  <div className="grid grid-cols-3 gap-4">
                    {[{ v: "07+", l: "Years" }, { v: "2000+", l: "Products" }, { v: "11", l: "Brands" }].map(({ v, l }) => (
                      <div key={l} className="text-center">
                        <p className="text-xl font-black text-[#0F62FE] font-heading">{v}</p>
                        <p className="text-gray-300 text-xs">{l}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-14 bg-[#F3F4F6]" data-testid="mission-vision-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-6">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
              className="p-8 bg-[#0F62FE] text-white">
              <Target size={32} className="mb-4 text-blue-200" />
              <h3 className="font-heading text-xl font-black mb-3">Our Mission</h3>
              <p className="text-blue-100 leading-relaxed text-sm">
                To deliver premium, enterprise-grade technology hardware and connectivity accessories that empower our clients to architect highly functional, highly secure, and dynamically scalable IT infrastructures.
              </p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }}
              className="p-8 bg-[#2D3748] text-white">
              <Eye size={32} className="mb-4 text-[#00BCD4]" />
              <h3 className="font-heading text-xl font-black mb-3">Our Vision</h3>
              <p className="text-gray-300 leading-relaxed text-sm">
                To stand as the absolute benchmark in high-volume technology distribution through persistent portfolio evolution, localized logistical superiority, and robust B2B alliance strategies.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-14 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl font-black text-[#111827] mb-3">Our Core Values</h2>
            <p className="text-[#4B5563] text-base max-w-md mx-auto">The principles that guide every transaction and relationship</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map(({ Icon, title, desc }, i) => (
              <motion.div key={title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }}
                className="p-6 border border-gray-200 hover:border-[#0F62FE] hover:shadow-md transition-all duration-200 bg-[#F3F4F6] group">
                <div className="w-10 h-10 bg-[#EFF6FF] flex items-center justify-center mb-4 group-hover:bg-[#0F62FE] transition-colors">
                  <Icon size={20} className="text-[#0F62FE] group-hover:text-white transition-colors" />
                </div>
                <h4 className="font-heading font-bold text-[#111827] text-sm mb-2">{title}</h4>
                <p className="text-xs text-[#4B5563] leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-14 bg-[#F3F4F6]" data-testid="timeline-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl font-black text-[#111827] mb-3">Our Journey</h2>
            <p className="text-[#4B5563] text-base">Milestones that define our growth</p>
          </div>
          <div className="relative">
            <div className="absolute left-[calc(50%-1px)] top-0 bottom-0 w-0.5 bg-gray-200 hidden md:block" />
            <div className="space-y-6">
              {TIMELINE.map(({ year, event }, i) => (
                <motion.div key={year} initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }}
                  className={`flex gap-6 items-start md:items-center ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}>
                  <div className={`flex-1 p-5 bg-white border border-gray-200 hover:border-[#0F62FE] transition-all ${i % 2 === 0 ? "md:text-right" : ""}`}>
                    <p className="text-sm text-[#4B5563] leading-relaxed">{event}</p>
                  </div>
                  <div className="relative z-10 shrink-0">
                    <div className="w-14 h-14 bg-[#0F62FE] flex items-center justify-center shadow-lg">
                      <span className="text-white font-black text-xs font-heading">{year}</span>
                    </div>
                  </div>
                  <div className="flex-1 hidden md:block" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Brochure CTA */}
      <section className="py-14 bg-[#2D3748]" data-testid="brochure-cta-section">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl lg:text-3xl font-black text-white mb-3">Download Our Company Profile</h2>
          <p className="text-gray-400 text-base mb-7 max-w-md mx-auto">
            Get our complete company brochure with full product catalogue, capabilities, and partner details.
          </p>
          <a
            href="/assets/jain-infotech-brochure.pdf"
            download
            data-testid="about-brochure-download-btn"
            className="inline-flex items-center gap-2 bg-[#0F62FE] hover:bg-[#0052CC] text-white font-semibold px-8 py-4 transition-all duration-200 hover:shadow-xl text-base"
          >
            <Download size={18} /> Download Brochure (PDF)
          </a>
        </div>
      </section>
    </div>
  );
}
